/**
 * The P&L table (TMMAPP-0111; MM-D-124 / MM-D-127): one table for the virtual account P&L
 * (Categories → P&L) and its envelope alias (the envelope's P&L tab), both the `VirtualAccountPnl`
 * shape. Months are columns (`md`+; `xs` shows Total only, `md` the last six with the category
 * column sticky, `lg` the whole window); income rows, then expense rows, each with its served
 * Uncategorized row and a subtotal; then Net as served. A BUDGET envelope's report adds its Budget
 * row (the budget in effect per month, MM-D-118). With `breakdown=envelope` each category row
 * expands into its envelopes (Unassigned for the virtual account's own part).
 */
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Pnl, PnlCategoryRow } from '@/api/pnl';
import { CategoryChip } from '@/components/CategoryChip';
import { ExpandIcon, NextIcon } from '@/components/icons';
import { MoneyText } from '@/components/MoneyText';
import { formatCivilMonthShort } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import { subtotalCells, totalOf } from './pnlCells';

/** `md` shows the last six months in view; the rest scroll. */
const MD_VISIBLE_MONTHS = 6;
const MONTH_COLUMN_WIDTH = 104;
const CATEGORY_COLUMN_WIDTH = 180;

const total = totalOf;

interface PnlTableProps {
  pnl: Pnl;
  currency: string | null;
  /** The envelope whose Budget row shows (its report); omitted on the virtual account's. */
  budgetEnvelopeId?: number;
  /** Category rows expand into their envelopes (`breakdown=envelope`). */
  expandable?: boolean;
  label: string;
}

export function PnlTable({
  pnl,
  currency,
  budgetEnvelopeId,
  expandable = false,
  label,
}: PnlTableProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const scroller = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());

  // `md`+: start scrolled to the end so the most recent months are in view (the DOM is the
  // external system here; a scroll position is not React state). Keyed on the response itself.
  useEffect(() => {
    const el = scroller.current;
    if (el !== null && md) el.scrollLeft = el.scrollWidth;
  }, [md, pnl]);

  const monthColumns = md ? pnl.months : [];
  const mdViewportWidth = CATEGORY_COLUMN_WIDTH + (MD_VISIBLE_MONTHS + 1) * MONTH_COLUMN_WIDTH;
  const stickySx = {
    position: 'sticky',
    insetInlineStart: 0,
    zIndex: 1,
    bgcolor: 'background.paper',
    minWidth: CATEGORY_COLUMN_WIDTH,
  } as const;
  const budget =
    budgetEnvelopeId === undefined
      ? null
      : (pnl.envelopes.find((e) => e.envelopeId === budgetEnvelopeId)?.budgetCells ?? null);
  const envelopeName = (envelopeId: number | null) =>
    envelopeId === null
      ? t('virtualAccount.unassigned')
      : (pnl.envelopes.find((e) => e.envelopeId === envelopeId)?.name ?? '');

  const valueCells = (cells: (number | null)[], rowTotal: number | null) => (
    <>
      {monthColumns.map((column, index) => (
        <TableCell key={column} align="right" sx={{ minWidth: MONTH_COLUMN_WIDTH }}>
          <MoneyText minor={cells[index] ?? null} currency={currency} />
        </TableCell>
      ))}
      <TableCell align="right" sx={{ minWidth: MONTH_COLUMN_WIDTH }}>
        <MoneyText minor={rowTotal} currency={currency} />
      </TableCell>
    </>
  );

  const labelRow = (key: string, text: string) => (
    <TableRow key={key}>
      {/* The label sits in the sticky column (a spanning sticky cell would scroll its text away). */}
      <TableCell sx={{ ...stickySx, fontWeight: 500 }}>{text}</TableCell>
      <TableCell colSpan={monthColumns.length + 1} />
    </TableRow>
  );

  const toggle = (categoryId: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });

  const categoryRows = (rows: PnlCategoryRow[]): ReactNode =>
    rows.map((row) => {
      const canExpand = expandable && row.byEnvelope.length > 0;
      const open = canExpand && expanded.has(row.categoryId);
      return (
        <Fragment key={`c-${row.categoryId}`}>
          <TableRow>
            <TableCell sx={stickySx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {canExpand && (
                  <IconButton
                    size="small"
                    aria-expanded={open}
                    aria-label={t(open ? 'pnl.collapseRow' : 'pnl.expandRow', { name: row.name })}
                    onClick={() => toggle(row.categoryId)}
                  >
                    {open ? <ExpandIcon /> : <NextIcon />}
                  </IconButton>
                )}
                <CategoryChip id={row.categoryId} name={row.name} />
              </Box>
            </TableCell>
            {valueCells(row.cells, row.totalMinor)}
          </TableRow>
          {open &&
            row.byEnvelope.map((part) => (
              <TableRow key={`c-${row.categoryId}-e-${part.envelopeId ?? 'u'}`}>
                <TableCell
                  sx={{ ...stickySx, paddingInlineStart: 6, color: tokens.text2, fontSize: 13 }}
                >
                  {envelopeName(part.envelopeId)}
                </TableCell>
                {valueCells(part.cells, total(part.cells))}
              </TableRow>
            ))}
        </Fragment>
      );
    });

  const uncategorizedRow = (key: string, cells: number[], rowTotal: number) => (
    <TableRow key={key}>
      <TableCell sx={{ ...stickySx, color: tokens.text2 }}>{t('envelope.uncategorized')}</TableCell>
      {valueCells(cells, rowTotal)}
    </TableRow>
  );

  const incomeSubtotal = subtotalCells(pnl.income, pnl.uncategorized.inCells);
  const expenseSubtotal = subtotalCells(pnl.expense, pnl.uncategorized.outCells);

  return (
    <TableContainer
      ref={scroller}
      sx={{
        overflowX: 'auto',
        // `md`: at most the last six months + Total in view (the category column is sticky);
        // earlier months scroll. `lg` lifts the cap and shows the whole window.
        maxWidth: { md: mdViewportWidth, lg: 'none' },
      }}
    >
      <Table size="small" aria-label={label}>
        <TableHead>
          <TableRow>
            {/* The head keeps the theme's `surface2` (`text2 / surface2`, audited). */}
            <TableCell sx={{ ...stickySx, bgcolor: tokens.surface2 }}>
              {t('envelope.colCategory')}
            </TableCell>
            {monthColumns.map((column) => (
              <TableCell key={column} align="right" sx={{ minWidth: MONTH_COLUMN_WIDTH }}>
                {formatCivilMonthShort(column, locale)}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ minWidth: MONTH_COLUMN_WIDTH }}>
              {t('envelope.pnlTotal')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {labelRow('income', t('envelope.pnlIncomeRows'))}
          {categoryRows(pnl.income)}
          {uncategorizedRow(
            'income-uncategorized',
            pnl.uncategorized.inCells,
            pnl.uncategorized.inTotalMinor,
          )}
          <TableRow>
            <TableCell sx={{ ...stickySx, fontWeight: 500 }}>{t('envelope.pnlSubtotal')}</TableCell>
            {valueCells(incomeSubtotal, total(incomeSubtotal))}
          </TableRow>
          {labelRow('expense', t('envelope.pnlExpenseRows'))}
          {categoryRows(pnl.expense)}
          {uncategorizedRow(
            'expense-uncategorized',
            pnl.uncategorized.outCells,
            pnl.uncategorized.outTotalMinor,
          )}
          <TableRow>
            <TableCell sx={{ ...stickySx, fontWeight: 500 }}>{t('envelope.pnlSubtotal')}</TableCell>
            {valueCells(expenseSubtotal, total(expenseSubtotal))}
          </TableRow>
          {budget !== null && (
            <TableRow>
              <TableCell sx={{ ...stickySx, color: tokens.text2 }}>{t('pnl.budgetRow')}</TableCell>
              {/* The budget in effect per month: a period's target, so it has no total. */}
              {valueCells(budget, null)}
            </TableRow>
          )}
          <TableRow>
            <TableCell sx={{ ...stickySx, fontWeight: 500 }}>{t('envelope.pnlNet')}</TableCell>
            {valueCells(pnl.net.cells, pnl.net.totalMinor)}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
